/**
 * Admin Audit Console — per docs/specs/admin-audit.md and
 * ADMIN_AUDIT_CONSOLE_SPEC.md.
 *
 * Admin roster plus normalized event/action search backed by `/api/admin/audit`.
 *
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-27-v2
 */
import { Download, RotateCcw, Search, Shield, ShieldAlert } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Chip } from '../components/chip';
import { useToast } from '../components/toast';
import { RoleButton } from '../components/role-gate';
import { useShell } from '../layout/shell';
import {
    downloadAdminAuditCsv,
    endAllImpersonations,
    useAdminAudit,
    useAdminAuditKpis,
    useAdmins,
    useImpersonations
} from '../../lib/api';
import { buildAuditQueryString, getAuditSource, getAuditTargetId } from '../../lib/audit';
import { useAdminSession } from '../../lib/auth';
import type { AdminAuditEntry, AdminRole, AuditQueryFilters } from '../../lib/types';
import { downloadCsv } from '../../lib/csv';
import { getHttpErrorMessage } from '../../lib/http';
import { AddAdminModal } from './add-admin-modal';
import { ManageAdminModal } from './manage-admin-modal';
import { AuditDetailPanel } from './audit-detail-panel';
import { ResourceHistoryPanel } from './resource-history-panel';

const roleTone = (role: AdminRole) =>
    role === 'owner' ? 'purple' : role === 'support' ? 'info' : 'muted';

const severityTone = (severity: AdminAuditEntry['severity']) =>
    severity === 'destructive' ? 'danger' : severity === 'sensitive' ? 'warning' : 'muted';

const PERMISSION_MATRIX = [
    { action: 'View roster', owner: 'yes', support: 'yes', read_only: 'yes' },
    { action: 'Add / change / remove', owner: 'yes', support: 'no', read_only: 'no' },
    { action: 'Force 2FA reset', owner: 'yes', support: 'no', read_only: 'no' },
    { action: 'View audit console', owner: 'yes', support: 'yes', read_only: 'yes' },
    { action: 'Export audit log', owner: 'yes + reason', support: 'no', read_only: 'no' },
    { action: 'Restore / undo action', owner: 'yes', support: 'scoped', read_only: 'no' },
    { action: 'End-all impersonations', owner: 'yes', support: 'no', read_only: 'no' }
] as const;

type AuditTab = 'audit' | 'impersonations';
type HistoryTarget = { type: string; id: string } | null;

export function AdminAuditPage() {
    const [params] = useSearchParams();
    const actor = useAdminSession();
    const queryClient = useQueryClient();
    const { toast } = useToast();
    const { setDetailPanel } = useShell();

    const [tab, setTab] = useState<AuditTab>('audit');
    const [search, setSearch] = useState(params.get('q') ?? '');
    const [source, setSource] = useState<'all' | 'event' | 'action'>(
        params.get('source') === 'event' || params.get('source') === 'action'
            ? (params.get('source') as 'event' | 'action')
            : 'all'
    );
    const [severity, setSeverity] = useState<'all' | AdminAuditEntry['severity']>(
        params.get('severity') === 'destructive' ||
            params.get('severity') === 'sensitive' ||
            params.get('severity') === 'routine'
            ? (params.get('severity') as AdminAuditEntry['severity'])
            : 'all'
    );
    const [fromDate, setFromDate] = useState(params.get('from') ?? '');
    const [toDate, setToDate] = useState(params.get('to') ?? '');
    const [resourceType, setResourceType] = useState(params.get('resource_type') ?? '');
    const [resourceId, setResourceId] = useState(params.get('resource_id') ?? '');
    const [actorUserId, setActorUserId] = useState(params.get('actor_user_id') ?? '');
    const [restorable, setRestorable] = useState<'all' | 'true' | 'false'>(
        params.get('restorable') === 'true' || params.get('restorable') === 'false'
            ? (params.get('restorable') as 'true' | 'false')
            : 'all'
    );
    const [selectedEntry, setSelectedEntry] = useState<AdminAuditEntry | null>(null);
    const [historyTarget, setHistoryTarget] = useState<HistoryTarget>(null);
    const [selectedAdminId, setSelectedAdminId] = useState<string | null>(null);
    const [addOpen, setAddOpen] = useState(false);
    const [endingImpersonations, setEndingImpersonations] = useState(false);

    const auditFilters = useMemo<AuditQueryFilters>(
        () => ({
            from: fromDate || undefined,
            to: toDate || undefined,
            severity,
            q: search.trim() || undefined,
            source,
            resource_type: resourceType.trim() || undefined,
            resource_id: resourceId.trim() || undefined,
            actor_user_id: actorUserId.trim() || undefined,
            restorable: restorable === 'all' ? 'all' : restorable === 'true' ? true : false,
            limit: 100
        }),
        [actorUserId, fromDate, resourceId, resourceType, restorable, search, severity, source]
    );

    const kpis = useAdminAuditKpis();
    const admins = useAdmins();
    const audit = useAdminAudit(auditFilters);
    const impersonations = useImpersonations();

    useEffect(() => {
        if (historyTarget) {
            setDetailPanel(
                <ResourceHistoryPanel
                    resourceType={historyTarget.type}
                    resourceId={historyTarget.id}
                    onClose={() => {
                        setHistoryTarget(null);
                        setDetailPanel(null);
                    }}
                />
            );
        } else if (selectedEntry) {
            setDetailPanel(
                <AuditDetailPanel
                    entry={selectedEntry}
                    onClose={() => {
                        setSelectedEntry(null);
                        setDetailPanel(null);
                    }}
                    onOpenResourceHistory={(type, id) => setHistoryTarget({ type, id })}
                />
            );
        } else {
            setDetailPanel(null);
        }

        return () => setDetailPanel(null);
    }, [historyTarget, selectedEntry, setDetailPanel]);

    const parseEntryTime = (value: string) => {
        const date = new Date(`${value.replace(' ', 'T')}:00`);
        const ts = date.getTime();
        return Number.isFinite(ts) ? ts : null;
    };

    const filteredImpersonations = (impersonations.data ?? []).filter((entry) => {
        const entryTime = parseEntryTime(entry.ts);
        const fromDateTs = fromDate ? new Date(`${fromDate}T00:00:00`).getTime() : null;
        const toDateTs = toDate ? new Date(`${toDate}T23:59:59.999`).getTime() : null;
        if (entryTime === null) return false;
        if (Number.isFinite(fromDateTs || NaN) && entryTime < (fromDateTs as number)) return false;
        if (Number.isFinite(toDateTs || NaN) && entryTime > (toDateTs as number)) return false;

        if (!search.trim()) return true;
        const normalized = search.trim().toLowerCase();
        return [entry.actor, entry.target_user, entry.reason].some((value) =>
            value.toLowerCase().includes(normalized)
        );
    });

    const clearFilters = () => {
        setSearch('');
        setSource('all');
        setSeverity('all');
        setFromDate('');
        setToDate('');
        setResourceType('');
        setResourceId('');
        setActorUserId('');
        setRestorable('all');
    };

    const filtersActive =
        !!search ||
        source !== 'all' ||
        severity !== 'all' ||
        !!fromDate ||
        !!toDate ||
        !!resourceType ||
        !!resourceId ||
        !!actorUserId ||
        restorable !== 'all';

    const ownerCount = admins.data?.filter((admin) => admin.role === 'owner').length ?? 0;
    const selectedAdmin = admins.data?.find((row) => row.id === selectedAdminId) ?? null;
    const auditRows = audit.data ?? [];
    const visibleRows = tab === 'audit' ? auditRows : filteredImpersonations;
    const totalRows = tab === 'audit' ? auditRows.length : (impersonations.data?.length ?? 0);

    const handleExport = async () => {
        if (!auditRows.length) return;

        const reason = prompt('Export reason (required):')?.trim();
        if (!reason) return;

        try {
            const params = new URLSearchParams(buildAuditQueryString(auditFilters).slice(1));
            params.set('reason', reason);
            const csv = await downloadAdminAuditCsv(params);
            downloadCsv(`admin-audit-${new Date().toISOString().slice(0, 10)}`, csv);
            toast('Server audit export generated', {
                detail: `${auditRows.length} visible rows; server applied the active filters.`
            });
        } catch (error) {
            toast(getHttpErrorMessage(error, 'Failed to export audit log'), { kind: 'danger' });
        }
    };

    const handleEndImpersonations = async () => {
        const confirmed = window.confirm(
            `End all active impersonation sessions and force re-auth for ${impersonations.data?.length ?? 0} tracked impersonation events?`
        );
        if (!confirmed) return;
        setEndingImpersonations(true);

        try {
            const result = await endAllImpersonations();
            await Promise.all([
                queryClient.invalidateQueries({
                    queryKey: ['admin', 'admin-audit', 'impersonations']
                }),
                queryClient.invalidateQueries({ queryKey: ['admin', 'admin-audit', 'kpis'] })
            ]);
            toast('Impersonation sessions ended', {
                detail:
                    typeof result.closed_count === 'number'
                        ? `${result.closed_count} session token${result.closed_count === 1 ? '' : 's'} marked ended`
                        : 'Panic action completed'
            });
        } catch (error) {
            toast('Failed to end impersonations', {
                kind: 'danger',
                detail:
                    error instanceof Error ? error.message : 'Impersonation end-all request failed'
            });
        } finally {
            setEndingImpersonations(false);
        }
    };

    return (
        <div className="stack-16">
            <header className="page-header">
                <div className="page-title-group">
                    <h1>Audit Console</h1>
                    <p>Search normalized event and action history from the admin audit feed.</p>
                </div>
                <div className="page-actions">
                    <RoleButton
                        scope="owner_only"
                        className="btn btn-danger"
                        onClick={handleEndImpersonations}
                        disabled={endingImpersonations}
                        title={
                            endingImpersonations
                                ? 'Ending impersonation sessions'
                                : 'End all active impersonation sessions'
                        }
                    >
                        <ShieldAlert size={14} />
                        {endingImpersonations ? 'Ending sessions...' : 'End all impersonations'}
                    </RoleButton>
                    <RoleButton
                        scope="owner_only"
                        className="btn btn-ghost"
                        onClick={handleExport}
                        disabled={tab !== 'audit' || !auditRows.length || endingImpersonations}
                    >
                        <Download size={14} /> Export log
                    </RoleButton>
                    <RoleButton
                        scope="owner_only"
                        className="btn btn-primary"
                        onClick={() => setAddOpen(true)}
                    >
                        Add admin
                    </RoleButton>
                </div>
            </header>

            <section className="kpi-grid kpi-grid-6" aria-label="Admin audit metrics">
                <KpiMini
                    label="Admin users"
                    value={
                        kpis.data
                            ? `${kpis.data.admins_total} (${kpis.data.owners}/${kpis.data.support}/${kpis.data.read_only})`
                            : '-'
                    }
                />
                <KpiMini
                    label="Destructive (7d)"
                    value={kpis.data?.destructive_7d.toString() ?? '-'}
                />
                <KpiMini
                    label="Sensitive (7d)"
                    value={kpis.data?.sensitive_7d?.toString() ?? '-'}
                />
                <KpiMini
                    label="Restorable (7d)"
                    value={kpis.data?.restorable_7d?.toString() ?? '-'}
                />
                <KpiMini
                    label="Event / action rows"
                    value={
                        kpis.data
                            ? `${kpis.data.event_rows_7d ?? '-'}/${kpis.data.action_rows_7d ?? '-'}`
                            : '-'
                    }
                />
                <KpiMini
                    label="Admins without 2FA"
                    value={kpis.data?.admins_without_2fa.toString() ?? '-'}
                    alert={!!kpis.data && kpis.data.admins_without_2fa > 0}
                />
            </section>

            <section className="card">
                <header className="card-header">
                    <h2 className="card-title">Admin users</h2>
                </header>
                <div className="table-wrap">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Admin</th>
                                <th>Role</th>
                                <th>Last active</th>
                                <th>Added</th>
                                <th>2FA</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {admins.data?.length ? (
                                admins.data.map((admin) => (
                                    <tr key={admin.id}>
                                        <td>
                                            <div className="user-cell">
                                                <div className="avatar">
                                                    {admin.name
                                                        .split(' ')
                                                        .map((part) => part[0])
                                                        .slice(0, 2)
                                                        .join('')}
                                                </div>
                                                <div className="user-meta">
                                                    <span className="user-name">{admin.name}</span>
                                                    <span className="user-sub">{admin.email}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <Chip tone={roleTone(admin.role)}>
                                                {admin.role.replace('_', ' ')}
                                            </Chip>
                                        </td>
                                        <td className="text-muted">{admin.last_active}</td>
                                        <td className="text-muted">{admin.added_at}</td>
                                        <td>
                                            <Chip tone={admin.tfa_enabled ? 'success' : 'danger'}>
                                                <Shield size={10} />
                                                {admin.tfa_enabled ? '2FA on' : '2FA off'}
                                            </Chip>
                                        </td>
                                        <td style={{ textAlign: 'right' }}>
                                            <RoleButton
                                                scope="owner_only"
                                                className="btn btn-sm btn-ghost"
                                                onClick={() => setSelectedAdminId(admin.id)}
                                            >
                                                Manage
                                            </RoleButton>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={6} className="text-muted" style={{ padding: 16 }}>
                                        No admins were returned from the live roster endpoint.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>

            <section className="card">
                <header className="card-header">
                    <h2 className="card-title">Permission matrix</h2>
                </header>
                <div className="table-wrap">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Action</th>
                                <th>Owner</th>
                                <th>Support</th>
                                <th>Read-only</th>
                            </tr>
                        </thead>
                        <tbody>
                            {PERMISSION_MATRIX.map((row) => (
                                <tr key={row.action}>
                                    <td>{row.action}</td>
                                    <td>{row.owner}</td>
                                    <td>{row.support}</td>
                                    <td>{row.read_only}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            <div className="segment-tabs">
                <button
                    role="tab"
                    aria-selected={tab === 'audit'}
                    onClick={() => setTab('audit')}
                    className="segment-tab"
                >
                    Audit feed
                </button>
                <button
                    role="tab"
                    aria-selected={tab === 'impersonations'}
                    onClick={() => setTab('impersonations')}
                    className="segment-tab"
                >
                    Impersonation log
                </button>
            </div>

            <div className="filter-bar audit-filter-bar">
                <label className="search">
                    <Search size={13} />
                    <input
                        type="search"
                        placeholder="Search actor, action, target, reason, request ID"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </label>
                <select
                    className="select"
                    aria-label="Source"
                    disabled={tab === 'impersonations'}
                    value={source}
                    onChange={(e) => setSource(e.target.value as typeof source)}
                >
                    <option value="all">All sources</option>
                    <option value="event">Event</option>
                    <option value="action">Action</option>
                </select>
                <select
                    className="select"
                    aria-label="Severity"
                    disabled={tab === 'impersonations'}
                    value={severity}
                    onChange={(e) =>
                        setSeverity(e.target.value as 'all' | AdminAuditEntry['severity'])
                    }
                >
                    <option value="all">All severity</option>
                    <option value="destructive">Destructive</option>
                    <option value="sensitive">Sensitive</option>
                    <option value="routine">Routine</option>
                </select>
                <select
                    className="select"
                    aria-label="Restorable"
                    disabled={tab === 'impersonations'}
                    value={restorable}
                    onChange={(e) => setRestorable(e.target.value as typeof restorable)}
                >
                    <option value="all">Any restore state</option>
                    <option value="true">Restorable</option>
                    <option value="false">Not restorable</option>
                </select>
                <input
                    className="select"
                    type="date"
                    aria-label="From date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                />
                <input
                    className="select"
                    type="date"
                    aria-label="To date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                />
                <input
                    className="select text-input"
                    type="text"
                    placeholder="resource type"
                    disabled={tab === 'impersonations'}
                    value={resourceType}
                    onChange={(e) => setResourceType(e.target.value)}
                />
                <input
                    className="select text-input"
                    type="text"
                    placeholder="resource ID"
                    disabled={tab === 'impersonations'}
                    value={resourceId}
                    onChange={(e) => setResourceId(e.target.value)}
                />
                <input
                    className="select text-input"
                    type="text"
                    placeholder="actor user ID"
                    disabled={tab === 'impersonations'}
                    value={actorUserId}
                    onChange={(e) => setActorUserId(e.target.value)}
                />
                <button
                    className="btn btn-ghost"
                    type="button"
                    onClick={clearFilters}
                    disabled={!filtersActive}
                >
                    Clear
                </button>
                <div className="count">
                    Showing {visibleRows.length} / {totalRows}
                </div>
            </div>

            {tab === 'audit' ? (
                <section className="card">
                    <div className="table-wrap">
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Time</th>
                                    <th>Actor</th>
                                    <th>Source</th>
                                    <th>Action</th>
                                    <th>Target</th>
                                    <th>Severity</th>
                                    <th>IP</th>
                                    <th>Restore</th>
                                </tr>
                            </thead>
                            <tbody>
                                {audit.isLoading ? (
                                    <tr>
                                        <td
                                            colSpan={8}
                                            className="text-muted"
                                            style={{ padding: 16 }}
                                        >
                                            Loading audit feed...
                                        </td>
                                    </tr>
                                ) : audit.error ? (
                                    <tr>
                                        <td colSpan={8} style={{ padding: 16 }}>
                                            <div className="empty-state">
                                                <div className="title">Audit feed unavailable</div>
                                                <div>
                                                    {audit.error instanceof Error
                                                        ? audit.error.message
                                                        : 'The audit endpoint returned an error.'}
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                ) : auditRows.length ? (
                                    auditRows.map((entry) => {
                                        const entrySource = getAuditSource(entry);
                                        const targetId = getAuditTargetId(entry);
                                        return (
                                            <tr
                                                key={`${entrySource}:${entry.id}`}
                                                aria-selected={selectedEntry?.id === entry.id}
                                                onClick={() => {
                                                    setHistoryTarget(null);
                                                    setSelectedEntry(entry);
                                                }}
                                            >
                                                <td className="mono text-muted">{entry.ts}</td>
                                                <td>
                                                    {entry.actor || entry.actor_id || 'system'}
                                                    <div className="sub">
                                                        <Chip tone={roleTone(entry.actor_role)}>
                                                            {entry.actor_role}
                                                        </Chip>
                                                    </div>
                                                </td>
                                                <td>
                                                    <Chip
                                                        tone={
                                                            entrySource === 'action'
                                                                ? 'purple'
                                                                : 'info'
                                                        }
                                                    >
                                                        {entrySource}
                                                    </Chip>
                                                </td>
                                                <td className="mono">{entry.action}</td>
                                                <td>
                                                    <div className="user-meta">
                                                        <span className="user-name">
                                                            {targetId}
                                                        </span>
                                                        <span className="user-sub">
                                                            {entry.target_type}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <Chip tone={severityTone(entry.severity)}>
                                                        {entry.severity}
                                                    </Chip>
                                                </td>
                                                <td className="mono text-muted">
                                                    {entry.ip || '-'}
                                                </td>
                                                <td>
                                                    {entry.restorable ? (
                                                        <Chip tone="success">
                                                            <RotateCcw size={10} /> restore
                                                        </Chip>
                                                    ) : (
                                                        <span className="text-muted">-</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan={8} style={{ padding: 16 }}>
                                            <div className="empty-state">
                                                <div className="title">
                                                    No audit rows match the current filters
                                                </div>
                                                <button
                                                    className="btn btn-ghost"
                                                    type="button"
                                                    onClick={clearFilters}
                                                    disabled={!filtersActive}
                                                >
                                                    Reset filters
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>
            ) : (
                <section className="card">
                    <div className="table-wrap">
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Time</th>
                                    <th>Admin</th>
                                    <th>Target user</th>
                                    <th>Duration</th>
                                    <th>Actions</th>
                                    <th>Reason</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredImpersonations.length ? (
                                    filteredImpersonations.map((entry) => (
                                        <tr key={entry.id}>
                                            <td className="mono text-muted">{entry.ts}</td>
                                            <td>{entry.actor}</td>
                                            <td>{entry.target_user}</td>
                                            <td className="mono">{entry.duration_min}m</td>
                                            <td className="numeric">{entry.actions_count}</td>
                                            <td className="text-muted">{entry.reason}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td
                                            colSpan={6}
                                            className="text-muted"
                                            style={{ padding: 16 }}
                                        >
                                            No impersonation activity has been logged yet.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}

            <AddAdminModal open={addOpen} onClose={() => setAddOpen(false)} />
            <ManageAdminModal
                admin={selectedAdmin}
                open={!!selectedAdmin}
                onClose={() => setSelectedAdminId(null)}
                ownerCount={ownerCount}
                currentAdminId={actor.admin_id}
            />
        </div>
    );
}

function KpiMini({ label, value, alert }: { label: string; value: string; alert?: boolean }) {
    return (
        <div className="kpi-tile" data-alert={alert ? 'true' : 'false'}>
            <span className="label">{label}</span>
            <span className="value">{value}</span>
        </div>
    );
}
